import { Button, DataTable, StatusBadge } from '@skylabs-digital/backoffice-kit';
import type { ColumnDef } from '@skylabs-digital/tables-react';
import { RotateCw } from 'lucide-react';
import { useMemo } from 'react';
import { formatDuration } from '../../lib/format';
import type { WebhookDelivery } from '../../schemas/webhook.schema';

/** El listado global: agrega app y tipo de evento a las columnas de la card por app. */
export function DeliveriesTable({
  rows,
  loading,
  error,
  onRedeliver,
}: {
  rows: WebhookDelivery[];
  loading: boolean;
  error: Error | null;
  onRedeliver: (d: WebhookDelivery) => void;
}) {
  const columns = useMemo<ColumnDef<WebhookDelivery>[]>(
    () => [
      {
        id: 'when',
        header: 'Requested at',
        cell: ({ row }) => (
          <span className="text-sm">{new Date(row.original.requestedAt).toLocaleString()}</span>
        ),
      },
      { accessorKey: 'appName', header: 'App' },
      { accessorKey: 'endpointName', header: 'Webhook' },
      {
        id: 'event',
        header: 'Event',
        cell: ({ row }) => (
          <span className="flex flex-col">
            {row.original.eventType ? (
              <StatusBadge tone="info" dot={false}>
                {row.original.eventType}
              </StatusBadge>
            ) : null}
            <code className="font-mono text-xs text-muted-foreground">{row.original.eventId}</code>
          </span>
        ),
      },
      { accessorKey: 'attempt', header: 'Attempt' },
      {
        id: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <StatusBadge tone={row.original.status === 'DELIVERED' ? 'success' : 'danger'}>
            {row.original.status === 'DELIVERED' ? 'delivered' : 'failed'}
          </StatusBadge>
        ),
      },
      {
        id: 'response',
        header: 'Response',
        cell: ({ row }) => (
          <span className="text-sm">
            {row.original.responseStatus ?? row.original.error ?? '—'}
            {row.original.durationMs ? ` · ${formatDuration(row.original.durationMs / 1000)}` : ''}
          </span>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <Button variant="outline" size="sm" onClick={() => onRedeliver(row.original)}>
            <RotateCw /> Redeliver
          </Button>
        ),
      },
    ],
    [onRedeliver],
  );
  return (
    <DataTable
      columns={columns}
      data={rows}
      isLoading={loading}
      error={error}
      hidePagination
      aria-label="Deliveries"
      empty={<p className="py-6 text-center text-sm text-muted-foreground">No deliveries match</p>}
    />
  );
}
